import React from "react";
import { IonIcon } from "@ionic/react";
import { closeOutline, businessOutline, locationOutline, timeOutline } from "ionicons/icons";
import { ShareableOrg } from "../../../services/orgSharing";
import { shareStatusOf } from "./useOrgSharing";

// A connected organization rendered as a line item INSIDE "Who can see my data", matching
// FollowerListItem's layout so orgs and people read as one list. Differentiated from a person by
// a business/location icon (vs initials) and a subtle "Organization" caption. A PENDING request
// (share_status 'pending') reads "Awaiting approval" (amber) with a "Cancel request" action
// instead of the X — the org can't see the patient yet. Both call onRemove; the caller decides.
//
// ⭐ F-492 (UAT, URS_6.8): the X is the patient's ONE way to LEAVE the organization — the caller
// (`useOrgRemoval`) confirms with the Leave dialog and calls DELETE .../association. It used to
// run the share-scoped DELETE .../share, which left an org-created link in place.
type Props = {
  org: ShareableOrg;
  /** "Parent · Location" for a location; the org's own name otherwise. */
  displayName: string;
  onRemove: (org: ShareableOrg) => void;
  disabled?: boolean;
};

const OrgFollowerRow: React.FC<Props> = ({ org, displayName, onRemove, disabled }) => {
  const isLocation = org.parent_id != null;
  const isPending = shareStatusOf(org) === "pending";
  return (
    <div
      className={`flex items-center justify-between p-4 border-b last:border-b-0 hover:bg-gray-50 transition-colors ${
        disabled ? "opacity-75" : ""
      }`}
    >
      <div className="flex items-center w-[65%] min-w-0">
        {/* Org icon avatar — mirrors the follower avatar circle, business/location glyph */}
        <div className="w-10 h-10 rounded-full bg-[#007C9E] text-white flex items-center justify-center font-bold shadow-sm flex-shrink-0">
          <IonIcon
            icon={isLocation ? locationOutline : businessOutline}
            style={{ fontSize: "20px" }}
          />
        </div>

        <div className="ml-3 flex-1 min-w-0">
          <div className="flex items-center">
            <span
              className="font-medium truncate block"
              style={{ maxWidth: "150px" }}
              title={displayName}
            >
              {displayName}
            </span>
          </div>
          <div className="mt-1 flex items-center text-xs text-gray-500">
            <span>{org.city ? `Organization · ${org.city}` : "Organization"}</span>
          </div>
          {isPending && (
            <div className="mt-1 flex items-center text-xs text-amber-600">
              <IonIcon icon={timeOutline} className="mr-1" style={{ fontSize: "14px" }} />
              <span>Awaiting approval</span>
            </div>
          )}
        </div>
      </div>

      <div className="flex justify-end items-center w-[35%] flex-shrink-0">
        {isPending ? (
          // Withdraw the still-pending request (kernel handles it on the same DELETE).
          <button
            className={`text-[#B45309] text-sm font-medium focus:outline-none px-2 py-1 rounded transition-colors ${
              disabled ? "opacity-50 cursor-not-allowed" : ""
            }`}
            onClick={() => onRemove(org)}
            disabled={disabled}
            aria-label={`Cancel your request to ${displayName}`}
          >
            Cancel request
          </button>
        ) : (
          <button
            className={`text-[#0778CF] focus:outline-none p-2 rounded transition-colors ${
              disabled ? "opacity-50 cursor-not-allowed" : ""
            }`}
            onClick={() => onRemove(org)}
            disabled={disabled}
            aria-label={`Leave ${displayName}`}
            title="Leave"
          >
            <IonIcon
              icon={closeOutline}
              style={{ width: "1.5em", height: "1.5em", fontSize: "15px" }}
            />
          </button>
        )}
      </div>
    </div>
  );
};

export default OrgFollowerRow;
